import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Sparkles, CheckCircle, XCircle, Trophy, Target } from 'lucide-react';
import BirdAvatar from './BirdAvatar';
import ReactionBar from './ReactionBar';

const JailBirdBonusQuestion = ({ question, onAnswer, score, correctCount, totalQuestions, nickname, avatarId }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const modalContentRef = React.useRef(null);

  const handleSubmit = () => {
    if (!selectedOption || showResult) return;
    
    const correct = selectedOption === question.correctCharge;
    setIsCorrect(correct);
    setShowResult(true);
    
    // Auto-scroll to show result within the modal
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
    
    // Proceed to next question after showing result
    setTimeout(() => {
      onAnswer(correct);
    }, 2500);
  };

  const handleOptionSelect = (option) => {
    if (showResult) return;
    setSelectedOption(option);
    
    // Auto-scroll to submit button within the modal
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollTo({ 
          top: modalContentRef.current.scrollHeight, 
          behavior: 'smooth' 
        });
      }
    }, 100);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-md">
      <motion.div
        ref={modalContentRef}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative w-full max-w-4xl max-h-[95vh] overflow-y-auto"
      >
        {/* Sparkly Gold Border Animation */}
        <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 rounded-2xl blur-lg opacity-75 animate-pulse" />
        <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-300 via-amber-400 to-yellow-300 rounded-2xl" />
        
        <Card className="relative bg-gradient-card border-4 border-amber-400 shadow-2xl overflow-hidden">
        {/* Sparkles in corners */}
        <div className="absolute top-2 left-2 z-10">
          <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" />
        </div>
        <div className="absolute top-2 right-2 z-10">
          <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
        </div>
        <div className="absolute bottom-2 left-2 z-10">
          <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        <div className="absolute bottom-2 right-2 z-10">
          <Sparkles className="w-5 h-5 text-amber-400 animate-pulse" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="relative p-4 sm:p-6">
          {/* Header - Same as regular questions */}
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div className="flex items-center gap-2">
              {nickname && avatarId && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card border border-border">
                  <BirdAvatar avatarId={avatarId} size={20} />
                  <span className="text-xs sm:text-sm font-semibold">{nickname}</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg bg-card border border-border">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="font-semibold text-xs sm:text-sm">{score}</span>
              </div>
              
              <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg bg-card border border-border">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                <span className="font-semibold text-xs sm:text-sm">{correctCount}/{totalQuestions}</span>
              </div>
            </div>
          </div>

          {/* JailBird Bonus Badge */}
          <div className="flex items-center justify-center mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 text-amber-950 font-bold text-sm sm:text-base shadow-lg border-2 border-amber-400">
              <Sparkles className="w-4 h-4" />
              JAILBIRD BONUS
              <Sparkles className="w-4 h-4" />
            </div>
          </div>

          {/* Mugshot - Same as regular questions */}
          <div className="mb-4 sm:mb-6">
            <div className="relative aspect-square max-w-xs sm:max-w-md mx-auto rounded-xl overflow-hidden border-2 border-border shadow-xl">
              <img
                src={question.imageUrl}
                alt={question.name}
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="text-center mt-4 bg-card rounded-lg p-3 sm:p-4 border border-border">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold mb-1">{question.name}</h2>
              <p className="text-sm sm:text-base text-muted-foreground font-semibold">Age: {question.age}</p>
            </div>

            {/* Emoji reactions */}
            {question.id && (
              <ReactionBar recordId={question.id} nickname={nickname} />
            )}
          </div>

          {/* Question - Same as regular questions */}
          <h3 className="text-xl sm:text-2xl md:text-3xl font-heading font-semibold text-center mb-4 sm:mb-6 px-2">
            What crime was <span className="text-primary font-bold">{question.name.includes(',') ? question.name.split(',')[1].trim().split(' ')[0] : question.name.split(' ')[0]}</span> arrested for?
          </h3>

          {/* Options - Same as regular questions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
            {question.options.map((option, index) => {
              const isSelected = selectedOption === option;
              const isThisCorrect = option === question.correctCharge;
              const showCorrect = showResult && isThisCorrect;
              const showWrong = showResult && isSelected && !isThisCorrect;

              return (
                <motion.button
                  key={index}
                  onClick={() => handleOptionSelect(option)}
                  disabled={showResult}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  whileHover={{ scale: showResult ? 1 : 1.02 }}
                  whileTap={{ scale: showResult ? 1 : 0.98 }}
                  className={`relative p-4 sm:p-5 rounded-xl border-2 text-left font-semibold text-base sm:text-lg transition-all ${
                    showCorrect
                      ? 'bg-gradient-to-br from-green-100 to-emerald-100 border-green-600 text-green-900 shadow-lg dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-400'
                      : showWrong
                      ? 'bg-gradient-to-br from-red-100 to-rose-100 border-destructive text-destructive shadow-lg dark:from-red-900/30 dark:to-rose-900/30'
                      : isSelected
                      ? 'bg-primary/10 border-primary text-primary shadow-lg'
                      : 'bg-card border-border hover:border-primary/50 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex-1">{option}</span>
                    {showCorrect && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring' }}
                      >
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
                      </motion.div>
                    )}
                    {showWrong && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring' }}
                      >
                        <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
                      </motion.div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Submit Button - Same as regular questions */}
          {!showResult && (
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={!selectedOption}
              className="w-full bg-gradient-primary hover-glow-primary text-primary-foreground font-bold text-lg sm:text-xl"
            >
              Submit Answer
            </Button>
          )}

          {/* Result Bubble - Same as regular questions */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`mt-4 p-4 sm:p-6 rounded-xl text-center ${
                  isCorrect 
                    ? 'bg-gradient-to-br from-green-100 to-emerald-100 border-2 border-green-600 dark:from-green-900/30 dark:to-emerald-900/30' 
                    : 'bg-gradient-to-br from-red-100 to-rose-100 border-2 border-red-600 dark:from-red-900/30 dark:to-rose-900/30'
                }`}
              >
                {isCorrect ? (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                      <h3 className="text-xl sm:text-2xl font-heading font-bold text-green-900 dark:text-green-400">
                        Correct!
                      </h3>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-green-800 dark:text-green-300">
                      <Sparkles className="w-5 h-5" />
                      <p className="text-lg sm:text-xl font-bold">+500 Bonus Points!</p>
                      <Sparkles className="w-5 h-5" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <XCircle className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                      <h3 className="text-xl sm:text-2xl font-heading font-bold text-red-900 dark:text-red-400">
                        Incorrect
                      </h3>
                    </div>
                    <p className="text-base sm:text-lg text-red-800 dark:text-red-300">
                      The correct answer was: <span className="font-bold">{question.correctCharge}</span>
                    </p>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
      </motion.div>
    </div>
  );
};

export default JailBirdBonusQuestion;
